import Decimal from 'decimal.js';
import type { DisposalDetail, FifoResult, LedgerEvent, Lot } from './types';

function sortLots(lots: Lot[]): Lot[] {
  return [...lots].sort((a, b) => a.acquiredAt - b.acquiredAt);
}

/**
 * Finland: FIFO disposal matching. Processes events in chronological order.
 */
export function runFifo(events: LedgerEvent[], _taxYear?: number): FifoResult {
  const warnings: string[] = [];
  const byAsset = new Map<string, Lot[]>();
  const details: DisposalDetail[] = [];
  let totalGainEur = new Decimal(0);
  let totalLossEur = new Decimal(0);

  const sorted = [...events].sort((a, b) => a.ts - b.ts);

  for (const ev of sorted) {
    if (ev.kind === 'internal_transfer') continue;
    if (ev.classification === 'IGNORE_NON_TAX') continue;
    if (ev.classification === 'UNKNOWN_REVIEW' && ev.eurValue == null) continue;
    if (ev.eurValue == null) {
      warnings.push(
        `Skipped ${ev.source} ${ev.kind} ${ev.asset} (${new Date(ev.ts).toISOString().slice(0, 10)}) — no EUR value yet${ev.ref ? ` ref ${ev.ref.slice(0, 14)}…` : ''}.`
      );
      continue;
    }
    const asset = ev.asset.toUpperCase();
    if (ev.kind === 'acquisition' || ev.kind === 'income') {
      const list = byAsset.get(asset) ?? [];
      list.push({
        asset,
        amount: ev.amount,
        costEur: ev.eurValue!,
        acquiredAt: ev.ts,
        lotBasisRepair: ev.lotBasisRepair,
        acquisitionProvenance: ev.acquisitionProvenance,
        acquisitionProvenanceFlag: ev.acquisitionProvenanceFlag,
      });
      byAsset.set(asset, list);
      continue;
    }

    let remaining = ev.amount;
    const lots = sortLots(byAsset.get(asset) ?? []);

    if (lots.length === 0) {
      const proceedsAll = ev.eurValue;
      warnings.push(
        `Disposal of ${asset} with no prior lots (${new Date(ev.ts).toISOString()}). Cost basis assumed 0.`
      );
      if (proceedsAll.gte(0)) totalGainEur = totalGainEur.plus(proceedsAll);
      else totalLossEur = totalLossEur.plus(proceedsAll.abs());
      details.push({
        asset,
        ts: ev.ts,
        disposed: remaining,
        proceedsEur: proceedsAll,
        costEur: new Decimal(0),
        gainEur: proceedsAll,
        source: ev.source,
        ref: ev.ref,
        csvMeta: ev.csvMeta,
        proceedsDerivation: ev.proceedsDerivation,
        valuationFlag: ev.valuationFlag,
        lotBasisRepair: undefined,
      });
      continue;
    }

    while (remaining.gt(0) && lots.length > 0) {
      const lot = lots[0];
      const take = Decimal.min(remaining, lot.amount);
      const costPortion = lot.costEur.mul(take).div(lot.amount);
      const proceedsPortion = ev.eurValue.mul(take).div(ev.amount);
      lot.amount = lot.amount.minus(take);
      lot.costEur = lot.costEur.minus(costPortion);
      remaining = remaining.minus(take);

      const gain = proceedsPortion.minus(costPortion);
      if (gain.gte(0)) totalGainEur = totalGainEur.plus(gain);
      else totalLossEur = totalLossEur.plus(gain.abs());

      details.push({
        asset,
        ts: ev.ts,
        disposed: take,
        proceedsEur: proceedsPortion,
        costEur: costPortion,
        gainEur: gain,
        source: ev.source,
        ref: ev.ref,
        lotAcquiredAt: lot.acquiredAt,
        csvMeta: ev.csvMeta,
        proceedsDerivation: ev.proceedsDerivation,
        valuationFlag: ev.valuationFlag,
        lotBasisRepair: lot.lotBasisRepair,
        acquisitionProvenance: lot.acquisitionProvenance,
        acquisitionProvenanceFlag: lot.acquisitionProvenanceFlag,
      });

      if (lot.amount.lte(0)) lots.shift();
    }

    byAsset.set(asset, lots);

    if (remaining.gt(0)) {
      const proceedsPortion = ev.eurValue.mul(remaining).div(ev.amount);
      warnings.push(
        `Insufficient ${asset} lots; ${remaining.toString()} units with ~€${proceedsPortion.toFixed(2)} proceeds (cost basis 0).`
      );
      if (proceedsPortion.gte(0)) totalGainEur = totalGainEur.plus(proceedsPortion);
      else totalLossEur = totalLossEur.plus(proceedsPortion.abs());
      details.push({
        asset,
        ts: ev.ts,
        disposed: remaining,
        proceedsEur: proceedsPortion,
        costEur: new Decimal(0),
        gainEur: proceedsPortion,
        source: ev.source,
        ref: ev.ref,
        csvMeta: ev.csvMeta,
        proceedsDerivation: ev.proceedsDerivation,
        valuationFlag: ev.valuationFlag,
        lotBasisRepair: undefined,
      });
    }
  }

  return {
    details,
    totalGainEur,
    totalLossEur,
    warnings,
  };
}
